<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { SurveyQuestion } from '@/types/surveyTypes'
import { Field, ErrorMessage } from 'vee-validate'
import { useSurvey, AnswerTarget } from '@/composables/onlineSurvey/useSurvey'

const { setAnswer, answers } = useSurvey()

const props = defineProps<{
  question: SurveyQuestion
  answerTarget?: AnswerTarget
}>()

const fieldName = computed(() => props.question.id)

// Initialisiere selectedValues mit bestehenden Antworten
const selectedValues = ref<string[]>([])

// Synchronisiere selectedValues mit bestehenden Antworten
const initializeValues = () => {
  const existingAnswer = answers.value.find((a) => a.id === props.question.id)
  if (existingAnswer && Array.isArray(existingAnswer.answer)) {
    selectedValues.value = [...existingAnswer.answer]
  } else {
    selectedValues.value = []
  }
}

// Initialisiere beim Mount
initializeValues()

// Watch für Änderungen in answers (z.B. beim Step-Wechsel)
watch(
  () => answers.value,
  () => {
    initializeValues()
  },
  { deep: true },
)

// Prüfung ob eine Option ausgewählt ist
const isSelected = (optionValue: string) => selectedValues.value.includes(optionValue)

// Prüfung ob weitere Auswahl möglich ist
const canSelectMore = computed(() => {
  return !props.question.max_select || selectedValues.value.length < props.question.max_select
})

const exclusiveActive = computed(() => {
  if (!props.question.options) return false
  return props.question.options.some((option) => option.exclusive && isSelected(option.value))
})

// Handler für Option-Klicks
const toggleOption = (optionValue: string) => {
  const index = selectedValues.value.indexOf(optionValue)

  if (index > -1) {
    // Option entfernen
    selectedValues.value.splice(index, 1)
  } else {
    // Option hinzufügen (prüfe max_select)
    if (
      (!props.question.max_select && !exclusiveActive.value) ||
      (selectedValues.value.length < (props.question.max_select ?? Infinity) &&
        !exclusiveActive.value)
    ) {
      selectedValues.value.push(optionValue)
    }
  }

  // Update Survey State
  setAnswer(props.question.id, props.question.label, [...selectedValues.value], props.answerTarget)
}

watch(exclusiveActive, (newVal) => {
  if (newVal) {
    // Wenn eine exklusive Option ausgewählt ist, entferne alle anderen
    const exclusiveOption = props.question.options?.find(
      (option) => option.exclusive && isSelected(option.value),
    )
    if (exclusiveOption) {
      selectedValues.value = [exclusiveOption.value]
      setAnswer(
        props.question.id,
        props.question.label,
        [...selectedValues.value],
        props.answerTarget,
      )
    }
  }
})
</script>

<template>
  <div class="field">
    <Field :name="fieldName" :model-value="selectedValues" class="is-hidden" />

    <!-- Bildbasierte Auswahl -->
    <div class="is-flex is-flex-direction-row is-multiline is-centered">
      <div
        v-for="option in props.question.options"
        :key="option.value"
        class="column is-3-desktop is-6-tablet is-12-mobile is-flex is-align-items-center is-justify-content-start is-flex-direction-column"
        :class="{
          'is-selected': isSelected(option.value),
          'is-disabled':
            (!isSelected(option.value) && !canSelectMore) ||
            (!isSelected(option.value) && exclusiveActive),
        }"
        @click="toggleOption(option.value)"
      >
        <!-- Bild Container -->
        <div class="image-container">
          <img
            :src="option.image_url"
            :alt="option.image_alt || option.label"
            class="choice-image"
          />
          <!-- Auswahl-Indikator -->
          <div v-if="isSelected(option.value)" class="selection-indicator is-size-5">✓</div>
        </div>

        <!-- Label unter dem Bild -->
        <p class="choice-label">{{ option.label }}</p>
      </div>
    </div>

    <!-- Validierungsfehler -->
    <ErrorMessage :name="fieldName" class="help is-danger" />

    <!-- Optional: Hinweis für min/max Auswahl -->
    <p v-if="props.question.min_select || props.question.max_select" class="help">
      <span v-if="props.question.min_select && props.question.max_select">
        Wählen Sie zwischen {{ props.question.min_select }} und
        {{ props.question.max_select }} Optionen.
      </span>
      <span v-else-if="props.question.min_select">
        Wählen Sie mindestens {{ props.question.min_select }} Option(en).
      </span>
      <span v-else-if="props.question.max_select">
        Wählen Sie maximal {{ props.question.max_select }} Option(en).
      </span>
    </p>

    <!-- Aktuell ausgewählte Anzahl -->
    <p v-if="selectedValues.length > 0" class="help has-text-weight-bold">
      {{ selectedValues.length }} Option(en) ausgewählt
    </p>
  </div>
</template>
